// timelapseQuality — 品質プリセット (標準 / 高画質) の解決 + 動画長 cap の
// メモリ予算逆算 (growth-timelapse-share-2026-07-02.md §5.5、consult 2026-07-06)。
//
// 背景 (§5.5): カスタム秒数の cap は元々 X の 140 秒固定だった。だが
// 「保存 → YouTube 等へアップ」動線は X の 140 秒と無関係で、長尺の需要がある。
// 尺で線形に増える負荷は (1) 生成時間 と (2) 出力 mp4 の in-memory 保持
// (`mp4Sink.ts` `fastStart:'in-memory'`、finalize の Blob 化で瞬間 2 倍) の
// 2 つだけ。**唯一の非有界バッファ = 出力 mp4** なので、cap は
// 「デバイスが安全に確保できるメモリ天井」から逆算する:
//
//   peakMemory = 2 × mp4Size = 2 × bitrateBytesPerSec × seconds   (2 = finalize 2倍)
//   ⇒ maxSeconds = floor(peakMemoryBudget / (2 × bitrateBytesPerSec))
//
// bitrate は当該プリセット × キャンバス寸法の**実際の出力寸法**から
// `selectBitrate` (= encoderConfig の width×height×fps×0.12 clamp) で算出する。
// よって「解像度↑ → bitrate↑ → 同じメモリ予算で maxSeconds↓」が自然に効き、
// 高画質プリセットほど cap が短くなる (§5.5「メモリ予算を固定し高画質ほど
// 尺 max を下げる」)。
//
// プラットフォーム判定 (isMobile) は**本コアではしない** — フロントの
// `isPhoneViewport` / `useLayoutPreference.isMobile` を引数で受ける (§task 制約)。
//
// 全て data → data の pure 関数 (DOM / WebCodecs 非依存) なので vitest node
// 環境で maxEdge/fps/maxSeconds をピン留めテストできる。

import { selectBitrate } from './encoderConfig.js';
import { HARD_MAX_OUTPUT_SECONDS } from './framePlan.js';
import { computeOutputSize, type FrameSize } from './frameScale.js';

/**
 * 品質プリセット (§5.5 確定、480p は不採用):
 *   - `'standard'` (既定): 720p 級 (maxEdge 1280)。想定 = X・一般共有。
 *   - `'high'`: キャンバス native 相当 (ダウンスケール無し、1080p 級以上)。
 *     想定 = YouTube・保存・アーカイブ。
 */
export type TimelapseQualityPreset = 'standard' | 'high';

export const DEFAULT_TIMELAPSE_QUALITY: TimelapseQualityPreset = 'standard';

/** 標準プリセットの出力長辺上限 (px) — 現行既定 720p 級 (§5.5)。 */
export const STANDARD_QUALITY_MAX_EDGE = 1280;

/**
 * 高画質プリセットの出力長辺上限 (px)。1080p 幅 = 「1080p 級以上」を満たす clean
 * な値 (§5.5)。`computeOutputSize` は決して upscale しない (scale ≤ 1) ので、
 * 長辺がこれ以下のキャンバスは **native 解像度そのまま** (ダウンスケール無し) =
 * 高画質の狙い。これを超える巨大キャンバス (Modern Phase2 の ~2000px 等) のみ
 * ここで頭打ちにして、1 フレームあたりの VideoFrame / 合成メモリと encode 負荷を
 * 有界に保つ。
 */
export const HIGH_QUALITY_MAX_EDGE = 1920;

/** 両プリセット共通の出力 fps (§5.5 — 2 プリセットとも 30fps)。 */
export const TIMELAPSE_QUALITY_FPS = 30;

/**
 * モバイルの**瞬間ピークメモリ予算** (bytes)。finalize の Blob 化で mp4 全体が
 * 一瞬 2 倍になる (ArrayBuffer + Blob) のを織り込んだ天井。
 *
 * 校正 (§5.5): 標準 720p (16:9, ~3.3Mbps) × モバイルで maxSeconds ≈ 240 秒
 * (ユーザー承認値) になるよう定めた。3.3Mbps ⇒ 414,720 B/s、finalize 2 倍で
 * 829,440 B/s ⇒ 200MB / 829,440 ≈ 241 秒 (≈240)。mp4 実体は ~100MB でこれを
 * 「モバイル安全予算」の基準にする、という §5.5 の記述と一致 (100MB mp4 +
 * finalize コピー 100MB = 200MB ピーク)。
 */
export const MOBILE_PEAK_MEMORY_BUDGET_BYTES = 200_000_000;

/**
 * デスクトップの瞬間ピークメモリ予算 (bytes)。デスクトップはメモリ余力が桁違い
 * なので、モバイル予算の 4 倍 (§5.5「デスクトップは予算を大きく (例 4倍程度)」)。
 * 結果として標準/デスクトップは {@link HARD_MAX_OUTPUT_SECONDS} 上限に張り付き、
 * 高画質でも数百秒が確保される。
 */
export const DESKTOP_PEAK_MEMORY_BUDGET_BYTES = 4 * MOBILE_PEAK_MEMORY_BUDGET_BYTES;

/**
 * 標準プリセットの動画長ハード上限 (秒) — **X 無料枠でそのまま投稿できる長さ**
 * (consult 2026-07-06)。
 *
 * 背景: メモリ予算逆算だけだと標準/デスクトップは ~542s 等になり、標準の想定
 * 行き先である X の**無料枠 (non-premium) 動画長上限 140秒 / 最大 512MB** を
 * 超えて弾かれてしまう。標準 = 「X 向けで迷わず通る」を保証するため、標準だけ
 * メモリ予算由来の maxSeconds に **min(…, 140)** を追加 clamp する。
 *
 * 高画質は長尺の行き先 (YouTube / **X Premium+ は最大4時間**) を想定するので
 * この clamp を掛けず、メモリ予算由来のまま (数百秒) を維持する。
 */
export const X_FREE_MAX_SECONDS = 140;

export interface ResolveTimelapseQualityInput {
  /**
   * スマホか。**本コアでは判定しない** — フロントの `isPhoneViewport` /
   * `useLayoutPreference.isMobile` (M1 と同一判定) をそのまま渡す。
   */
  readonly isMobile: boolean;
  /** 部屋キャンバス幅 (`roomMeta.settings.canvasWidth`)。 */
  readonly canvasWidth: number;
  /** 部屋キャンバス高 (`roomMeta.settings.canvasHeight`)。 */
  readonly canvasHeight: number;
}

/**
 * プリセット × プラットフォーム × キャンバス寸法から解決した生成パラメータ。
 * フロントはこれ 1 つで「maxEdge/fps を生成リクエストへ、maxSeconds を
 * カスタム秒数入力の上限へ」を全部得られる。
 */
export interface ResolvedTimelapseQuality {
  readonly preset: TimelapseQualityPreset;
  /** 出力長辺上限 (px) — 生成リクエストの `maxEdge` にそのまま渡す。 */
  readonly maxEdge: number;
  /** 出力 fps — 生成リクエストの `fps` にそのまま渡す。 */
  readonly fps: number;
  /**
   * この設定でメモリ安全に生成できる最大動画長 (秒、整数)。カスタム秒数入力の
   * 上限 (clamp) + 生成リクエストの `maxSeconds` (framePlan の maxFrames 駆動) に使う。
   */
  readonly maxSeconds: number;
}

const presetMaxEdge = (preset: TimelapseQualityPreset): number =>
  preset === 'high' ? HIGH_QUALITY_MAX_EDGE : STANDARD_QUALITY_MAX_EDGE;

/**
 * プリセット × プラットフォーム × キャンバス寸法 → `{ maxEdge, fps, maxSeconds }`。
 *
 * maxSeconds はメモリ予算からの逆算 (モジュール冒頭の式) を
 * `[1, HARD_MAX_OUTPUT_SECONDS]` に clamp したもの。予算計算が壊れても
 * ハード上限が暴走を止める。
 *
 * `canvasWidth/Height` が不正 (≤0 等) の場合は `computeOutputSize` が `TypeError`
 * を投げる (呼び出し側バグ扱い、リポの throw-for-bugs 慣習)。
 */
export const resolveTimelapseQuality = (
  preset: TimelapseQualityPreset,
  input: ResolveTimelapseQualityInput,
): ResolvedTimelapseQuality => {
  const maxEdge = presetMaxEdge(preset);
  const fps = TIMELAPSE_QUALITY_FPS;

  // 実際の出力寸法 → 実 bitrate。native 相当 (upscale 無し) はここで自然に効く。
  const output: FrameSize = computeOutputSize(input.canvasWidth, input.canvasHeight, maxEdge);
  const bitrateBps = selectBitrate({ width: output.width, height: output.height, fps });
  const bitrateBytesPerSec = bitrateBps / 8;

  const peakBudget = input.isMobile
    ? MOBILE_PEAK_MEMORY_BUDGET_BYTES
    : DESKTOP_PEAK_MEMORY_BUDGET_BYTES;

  // finalize の瞬間 2 倍を織り込む (peak = 2 × mp4Size = 2 × bitrateBytes × sec)。
  const rawSeconds = Math.floor(peakBudget / (2 * bitrateBytesPerSec));
  const budgetSeconds = Math.max(1, Math.min(HARD_MAX_OUTPUT_SECONDS, rawSeconds));

  // 標準プリセット (X 向け) だけ X 無料枠 140秒で追加 clamp。高画質 (YouTube・
  // X Premium 長尺) はメモリ予算由来のまま (consult 2026-07-06)。モバイル等で
  // 予算由来が既に <140 ならそのまま (min が効かない) = 従来値を温存。
  const maxSeconds =
    preset === 'standard' ? Math.min(budgetSeconds, X_FREE_MAX_SECONDS) : budgetSeconds;

  return { preset, maxEdge, fps, maxSeconds };
};

// ── 出力寸法 + 推定ファイルサイズのライブ計算 (§5.5 A) ──────────────────────
//
// 品質プリセットを切り替えても UI にほぼ変化がなく差が伝わらない問題への対応。
// 出力サイズは `computeOutputSize` が **決して upscale しない** (scale ≤ 1) ので、
// ソース長辺が標準 maxEdge (1280) 以下だと標準/高画質が同一寸法 = 同一サイズになる。
// これを「行き先ラベル」だけで隠すと差が無いことが分からないので、各プリセットの
// **実際の出力寸法**と**推定ファイルサイズ**を具体値で並べて見せる。
//
// 重要 (task 制約): ここで使う `sourceWidth/Height` は**プレビュー/生成と同じ
// sourceRect** (自動クロップ ON = クロップ領域、OFF = キャンバス全体) をフロントが
// 渡す。`computeOutputSize` + `selectBitrate` の合成なので本番エンコードと寸法・
// bitrate がズレない (§5.5 の maxSeconds 逆算と同じ経路を使う)。

export interface EstimateTimelapseOutputInput {
  readonly preset: TimelapseQualityPreset;
  /**
   * 出力ソース幅 — **プレビュー/生成と同じ sourceRect** の幅 (自動クロップ ON =
   * クロップ領域幅、OFF = キャンバス幅)。フロントが `previewCrop ?? canvas` から渡す。
   */
  readonly sourceWidth: number;
  readonly sourceHeight: number;
  /**
   * 出力尺 (秒)。duration モードは選択秒数。倍速モードは出力長が編集時に確定しない
   * ので省略 → `estimatedBytes` は null (UI 側でサイズを「—」に degrade)。
   */
  readonly durationSeconds?: number;
}

export interface EstimatedTimelapseOutput {
  /** 実際の出力幅 (px、偶数・never-upscale)。`computeOutputSize` と同一。 */
  readonly width: number;
  readonly height: number;
  /** 実 bitrate (bps) — `selectBitrate` と同一 (推定サイズの母数)。 */
  readonly bitrateBps: number;
  /**
   * 推定ファイルサイズ (bytes) = `bitrateBps / 8 × durationSeconds`。実エンコードは
   * シーン複雑度で前後するので UI は「〜 / 推定」を明記する。`durationSeconds`
   * 省略 (倍速モード) は null。
   */
  readonly estimatedBytes: number | null;
}

/**
 * プリセット × ソース寸法 (+ 尺) → 実出力寸法・bitrate・推定サイズ。
 * `computeOutputSize` (never-upscale) → `selectBitrate` (§5.5 の逆算と同経路) を
 * 合成した pure ヘルパ。`sourceWidth/Height` が不正 (≤0) なら `computeOutputSize`
 * が `TypeError` を投げる (呼び出し側バグ扱い)。
 */
export const estimateTimelapseOutput = (
  input: EstimateTimelapseOutputInput,
): EstimatedTimelapseOutput => {
  const maxEdge = presetMaxEdge(input.preset);
  const output = computeOutputSize(input.sourceWidth, input.sourceHeight, maxEdge);
  const bitrateBps = selectBitrate({
    width: output.width,
    height: output.height,
    fps: TIMELAPSE_QUALITY_FPS,
  });
  const estimatedBytes =
    input.durationSeconds !== undefined &&
    Number.isFinite(input.durationSeconds) &&
    input.durationSeconds > 0
      ? Math.round((bitrateBps / 8) * input.durationSeconds)
      : null;
  return { width: output.width, height: output.height, bitrateBps, estimatedBytes };
};

/**
 * 高画質プリセットの出力寸法が標準と同一になるか (§5.5 C — サイレント no-op 検出)。
 * `computeOutputSize` は never-upscale なので、ソース長辺が標準 maxEdge (1280) 以下
 * だと高画質 (maxEdge 1920) でも縮小されず標準と同じ native 寸法に落ちる。この絵で
 * 高画質を選んでも標準と変わらないことを UI で注記するための判定。
 */
export const isHighQualitySameAsStandard = (sourceWidth: number, sourceHeight: number): boolean => {
  const std = computeOutputSize(sourceWidth, sourceHeight, STANDARD_QUALITY_MAX_EDGE);
  const high = computeOutputSize(sourceWidth, sourceHeight, HIGH_QUALITY_MAX_EDGE);
  return std.width === high.width && std.height === high.height;
};

/**
 * 生成に体感で時間がかかり始める秒数閾値 (§5.5 A「120秒超の指定には『生成に
 * 数分かかります』ヒント」)。生成時間は尺に線形 (per-frame composite ×
 * frame数)。モバイル CPU では 300s で 5-10 分になりうる。フロントの
 * ヒント表示のための pure な閾値 + 述語。
 */
export const LONG_GENERATION_HINT_SECONDS = 120;

/** `seconds` が {@link LONG_GENERATION_HINT_SECONDS} を超えるか (ヒント表示判定)。 */
export const shouldHintLongGeneration = (seconds: number): boolean =>
  Number.isFinite(seconds) && seconds > LONG_GENERATION_HINT_SECONDS;
